import { useState, useEffect, useCallback } from "react";
import { useOutletContext } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Receipt, RefreshCw, Download, Calendar, Building2, Search,
  FileText, Mail, DollarSign, TrendingUp, Clock, Check, AlertCircle,
  Filter, ChevronDown, Send, Eye, Printer
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const BACKEND_URL = ((typeof window !== 'undefined' && window.location.hostname.includes('seatpilot.co.uk')) ? 'https://seatpilot.co.uk' : (process.env.REACT_APP_BACKEND_URL || 'https://seatpilot.co.uk'));
const API = `${BACKEND_URL}/api`;

export default function BackofficeAccounts() {
  const { theme } = useOutletContext();
  const isDark = theme === 'dark';

  const [loading, setLoading] = useState(true);
  const [invoices, setInvoices] = useState([]);
  const [usageReports, setUsageReports] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [stats, setStats] = useState({
    total_revenue: 0,
    pending_amount: 0,
    paid_invoices: 0,
    pending_invoices: 0
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("all");

  // Create Invoice Dialog
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newInvoice, setNewInvoice] = useState({
    company_id: "",
    amount: 0,
    description: "",
    due_date: "",
    type: "subscription"
  });
  const [creating, setCreating] = useState(false);

  // Usage Details Dialog
  const [showUsageDialog, setShowUsageDialog] = useState(false);
  const [selectedUsage, setSelectedUsage] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);
const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [invoicesRes, companiesRes, usageRes] = await Promise.all([
        axios.get(`${API}/invoices/admin/all`, { withCredentials: true }).catch(() => ({ data: { invoices: [] } })),
        axios.get(`${API}/subscriptions/admin/companies`, { withCredentials: true }).catch(() => ({ data: { companies: [] } })),
        axios.get(`${API}/billing/admin/usage-reports`, { withCredentials: true }).catch(() => ({ data: { reports: [] } }))
      ]);

      setInvoices(invoicesRes.data.invoices || []);
      setCompanies(companiesRes.data.companies || []);
      setUsageReports(usageRes.data.reports || []);

      // Calculate stats - use 'total' field as primary, fallback to 'amount'
      const invoicesList = invoicesRes.data.invoices || [];
      const paid = invoicesList.filter(i => i.status === 'paid');
      const pending = invoicesList.filter(i => i.status === 'pending' || i.status === 'sent');
      
      setStats({
        total_revenue: paid.reduce((sum, i) => sum + (i.total || i.amount || 0), 0),
        pending_amount: pending.reduce((sum, i) => sum + (i.total || i.amount || 0), 0),
        paid_invoices: paid.length,
        pending_invoices: pending.length
      });
    } catch (error) {
      console.error("Failed to fetch accounts data:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric', month: 'short', year: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    const badges = {
      paid: { color: isDark ? 'bg-green-500/20 text-green-400' : 'bg-green-100 text-green-600', icon: Check },
      pending: { color: isDark ? 'bg-amber-500/20 text-amber-400' : 'bg-amber-100 text-amber-600', icon: Clock },
      sent: { color: isDark ? 'bg-blue-500/20 text-blue-400' : 'bg-blue-100 text-blue-600', icon: Mail },
      overdue: { color: isDark ? 'bg-red-500/20 text-red-400' : 'bg-red-100 text-red-600', icon: AlertCircle },
      draft: { color: isDark ? 'bg-slate-500/20 text-slate-400' : 'bg-gray-100 text-gray-600', icon: FileText }
    };
    const badge = badges[status] || badges.draft;
    const Icon = badge.icon;
    return (
      <span className={`px-2 py-1 rounded text-xs font-medium flex items-center gap-1 ${badge.color}`}>
        <Icon className="w-3 h-3" />
        {status}
      </span>
    );
  };

  const createInvoice = async () => {
    if (!newInvoice.company_id || newInvoice.amount <= 0) {
      toast.error("Please select a company and enter an amount");
      return;
    }
    setCreating(true);
    try {
      await axios.post(`${API}/invoices/admin/create`, newInvoice, { withCredentials: true });
      toast.success("Invoice created successfully");
      setShowCreateDialog(false);
      setNewInvoice({ company_id: "", amount: 0, description: "", due_date: "", type: "subscription" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create invoice");
    } finally {
      setCreating(false);
    }
  };

  const sendInvoice = async (invoiceId) => {
    try {
      await axios.post(`${API}/invoices/${invoiceId}/send`, {}, { withCredentials: true });
      toast.success("Invoice sent successfully");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send invoice");
    }
  };

  const markAsPaid = async (invoiceId) => {
    try {
      await axios.put(`${API}/invoices/admin/${invoiceId}/status`, { status: 'paid' }, { withCredentials: true });
      toast.success("Invoice marked as paid");
      fetchData();
    } catch (error) {
      toast.error("Failed to update invoice");
    }
  };

  const downloadInvoice = async (invoiceId, invoiceNumber) => {
    try {
      const response = await axios.get(`${API}/invoices/${invoiceId}/pdf`, {
        withCredentials: true,
        responseType: 'blob'
      });
      
      // Check if the response is actually a PDF
      const contentType = response.headers['content-type'];
      const contentDisposition = response.headers['content-disposition'];
      const isPDF = contentType?.includes('application/pdf');
      const extension = isPDF ? 'pdf' : 'html';
      
      // Try to get filename from Content-Disposition header first
      let filename = invoiceNumber || `invoice-${invoiceId}`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename=([^;]+)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '').replace(/\.pdf$/i, '');
        }
      }
      
      const blob = new Blob([response.data], { type: contentType || 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${filename}.${extension}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      // Cleanup the blob URL
      window.URL.revokeObjectURL(url);
      toast.success(`Downloaded ${filename}.${extension}`);
    } catch (error) {
      console.error("Download error:", error);
      toast.error("Failed to download invoice");
    }
  };

  const filteredInvoices = invoices.filter(invoice => {
    if (statusFilter !== 'all' && invoice.status !== statusFilter) return false;
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      const companyName = invoice.company_name || '';
      const invoiceId = invoice.invoice_id || '';
      if (!companyName.toLowerCase().includes(search) && !invoiceId.toLowerCase().includes(search)) {
        return false;
      }
    }
    return true;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className={`text-2xl font-bold flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            <Receipt className="w-6 h-6 text-violet-500" />
            Accounts & Billing
          </h1>
          <p className={`mt-1 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Manage invoices, billing, and usage reports</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={fetchData}>
            <RefreshCw className="w-4 h-4 mr-1" /> Refresh
          </Button>
          <Button size="sm" onClick={() => setShowCreateDialog(true)}>
            <FileText className="w-4 h-4 mr-1" /> Create Invoice
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className={`p-4 ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${isDark ? 'bg-green-500/20' : 'bg-green-100'}`}>
              <TrendingUp className="w-5 h-5 text-green-500" />
            </div>
            <div>
              <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Total Revenue</p>
              <p className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{formatCurrency(stats.total_revenue)}</p>
            </div>
          </div>
        </Card>
        <Card className={`p-4 ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${isDark ? 'bg-amber-500/20' : 'bg-amber-100'}`}>
              <Clock className="w-5 h-5 text-amber-500" />
            </div>
            <div>
              <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Pending</p>
              <p className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{formatCurrency(stats.pending_amount)}</p>
            </div>
          </div>
        </Card>
        <Card className={`p-4 ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${isDark ? 'bg-violet-500/20' : 'bg-violet-100'}`}>
              <Check className="w-5 h-5 text-violet-500" />
            </div>
            <div>
              <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Paid Invoices</p>
              <p className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{stats.paid_invoices}</p>
            </div>
          </div>
        </Card>
        <Card className={`p-4 ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${isDark ? 'bg-blue-500/20' : 'bg-blue-100'}`}>
              <Mail className="w-5 h-5 text-blue-500" />
            </div>
            <div>
              <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Pending Invoices</p>
              <p className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{stats.pending_invoices}</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="invoices" className="space-y-4">
        <TabsList className={isDark ? 'bg-slate-800' : 'bg-gray-100'}>
          <TabsTrigger value="invoices">Invoices</TabsTrigger>
          <TabsTrigger value="usage">Usage Reports</TabsTrigger>
          <TabsTrigger value="yearly">Year End Reports</TabsTrigger>
        </TabsList>

        {/* Invoices Tab */}
        <TabsContent value="invoices">
          <Card className={`p-6 ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-3 mb-6">
              <div className="relative flex-1">
                <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${isDark ? 'text-slate-500' : 'text-gray-400'}`} />
                <Input
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search invoices..."
                  className={`pl-9 ${isDark ? 'bg-slate-700 border-slate-600' : ''}`}
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-40">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="paid">Paid</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="sent">Sent</SelectItem>
                  <SelectItem value="overdue">Overdue</SelectItem>
                  <SelectItem value="draft">Draft</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Invoice List */}
            {loading ? (
              <div className="flex items-center justify-center h-40">
                <RefreshCw className="w-8 h-8 animate-spin text-violet-500" />
              </div>
            ) : filteredInvoices.length === 0 ? (
              <div className="text-center py-12">
                <FileText className={`w-12 h-12 mx-auto mb-3 ${isDark ? 'text-slate-600' : 'text-gray-300'}`} />
                <p className={isDark ? 'text-slate-400' : 'text-gray-500'}>No invoices found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className={`border-b ${isDark ? 'border-slate-700' : 'border-gray-200'}`}>
                      <th className={`text-left py-3 px-4 text-sm font-medium ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Invoice</th>
                      <th className={`text-left py-3 px-4 text-sm font-medium ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Company</th>
                      <th className={`text-left py-3 px-4 text-sm font-medium ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Amount</th>
                      <th className={`text-left py-3 px-4 text-sm font-medium ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Date</th>
                      <th className={`text-left py-3 px-4 text-sm font-medium ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Due</th>
                      <th className={`text-left py-3 px-4 text-sm font-medium ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Status</th>
                      <th className={`text-left py-3 px-4 text-sm font-medium ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredInvoices.map(invoice => (
                      <tr key={invoice.invoice_id} className={`border-b ${isDark ? 'border-slate-700 hover:bg-slate-700/50' : 'border-gray-100 hover:bg-gray-50'}`}>
                        <td className={`py-3 px-4 font-mono text-sm ${isDark ? 'text-violet-400' : 'text-violet-600'}`}>
                          {invoice.invoice_number || invoice.invoice_id?.slice(0, 12)}
                        </td>
                        <td className={`py-3 px-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          <div className="flex items-center gap-2">
                            <Building2 className={`w-4 h-4 ${isDark ? 'text-slate-400' : 'text-gray-400'}`} />
                            {invoice.company_name || 'Unknown'}
                          </div>
                        </td>
                        <td className={`py-3 px-4 font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          {formatCurrency(invoice.total || invoice.amount || 0)}
                        </td>
                        <td className={`py-3 px-4 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                          {formatDate(invoice.created_at)}
                        </td>
                        <td className={`py-3 px-4 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                          {formatDate(invoice.due_date)}
                        </td>
                        <td className="py-3 px-4">
                          {getStatusBadge(invoice.status)}
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex gap-1">
                            <Button variant="ghost" size="sm" onClick={() => downloadInvoice(invoice.invoice_id, invoice.invoice_number)} title="Download PDF">
                              <Download className="w-4 h-4" />
                            </Button>
                            {(invoice.status === 'draft' || invoice.status === 'pending') && (
                              <Button variant="ghost" size="sm" onClick={() => sendInvoice(invoice.invoice_id)} title="Send Invoice via Email">
                                <Send className="w-4 h-4" />
                              </Button>
                            )}
                            {invoice.status !== 'paid' && (
                              <Button variant="ghost" size="sm" onClick={() => markAsPaid(invoice.invoice_id)} title="Mark as Paid" className="text-green-500">
                                <Check className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </TabsContent>

        {/* Usage Reports Tab */}
        <TabsContent value="usage">
          <Card className={`p-6 ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>Monthly Usage Reports</h3>
                <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                  Usage reports for Pay-As-You-Go customers showing seat usage and charges.
                </p>
              </div>
              <Button variant="outline" size="sm" onClick={() => window.open(`${API}/invoices/admin/reports/usage-export?month=${new Date().toISOString().slice(0, 7)}`, '_blank')}>
                <Download className="w-4 h-4 mr-1" /> Export CSV
              </Button>
            </div>
            {usageReports.length === 0 ? (
              <div className="text-center py-12">
                <DollarSign className={`w-12 h-12 mx-auto mb-3 ${isDark ? 'text-slate-600' : 'text-gray-300'}`} />
                <p className={isDark ? 'text-slate-400' : 'text-gray-500'}>No usage reports available</p>
              </div>
            ) : (
              <div className="space-y-4">
                {usageReports.map((report, idx) => (
                  <div key={idx} className={`p-4 rounded-lg border ${isDark ? 'bg-slate-700/50 border-slate-600' : 'bg-gray-50 border-gray-200'}`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{report.company_name}</h4>
                        <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>{report.period}</p>
                      </div>
                      <div className="text-right">
                        <p className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{formatCurrency(report.total_amount)}</p>
                        <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>{report.total_seats?.toLocaleString()} seats</p>
                      </div>
                    </div>
                    <div className="mt-3 flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => { setSelectedUsage(report); setShowUsageDialog(true); }}>
                        <Eye className="w-4 h-4 mr-1" /> View Details
                      </Button>
                      <Button variant="outline" size="sm">
                        <Send className="w-4 h-4 mr-1" /> Send Bill
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </TabsContent>

        {/* Year End Reports Tab */}
        <TabsContent value="yearly">
          <Card className={`p-6 ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
            <h3 className={`font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>Year End Reports</h3>
            <p className={`mb-6 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
              Generate comprehensive reports for accounting and year-end filing.
            </p>
            
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              <div className={`p-5 rounded-lg border ${isDark ? 'bg-slate-700/50 border-slate-600' : 'bg-gray-50 border-gray-200'}`}>
                <h4 className={`font-medium mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>Revenue Summary</h4>
                <p className={`text-sm mb-4 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Complete revenue breakdown by month and customer</p>
                <Button variant="outline" size="sm" className="w-full" onClick={() => window.open(`${API}/invoices/admin/reports/revenue-summary?year=${new Date().getFullYear()}`, '_blank')}>
                  <Download className="w-4 h-4 mr-2" /> Download CSV
                </Button>
              </div>
              
              <div className={`p-5 rounded-lg border ${isDark ? 'bg-slate-700/50 border-slate-600' : 'bg-gray-50 border-gray-200'}`}>
                <h4 className={`font-medium mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>Invoice Report</h4>
                <p className={`text-sm mb-4 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>All invoices with payment status</p>
                <Button variant="outline" size="sm" className="w-full" onClick={() => window.open(`${API}/invoices/admin/reports/invoice-report?year=${new Date().getFullYear()}`, '_blank')}>
                  <Download className="w-4 h-4 mr-2" /> Download CSV
                </Button>
              </div>
              
              <div className={`p-5 rounded-lg border ${isDark ? 'bg-slate-700/50 border-slate-600' : 'bg-gray-50 border-gray-200'}`}>
                <h4 className={`font-medium mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>VAT Report</h4>
                <p className={`text-sm mb-4 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>VAT collected and breakdown by region</p>
                <Button variant="outline" size="sm" className="w-full" onClick={() => window.open(`${API}/invoices/admin/reports/vat-report?year=${new Date().getFullYear()}`, '_blank')}>
                  <Download className="w-4 h-4 mr-2" /> Download CSV
                </Button>
              </div>
            </div>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create Invoice Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create Invoice</DialogTitle>
            <DialogDescription>Create a new invoice for a customer</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>Company *</label>
              <Select value={newInvoice.company_id} onValueChange={(v) => setNewInvoice({...newInvoice, company_id: v})}>
                <SelectTrigger>
                  <SelectValue placeholder="Select company" />
                </SelectTrigger>
                <SelectContent>
                  {companies.map(company => (
                    <SelectItem key={company.company_id} value={company.company_id}>
                      {company.name || company.company_id}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>Amount (£) *</label>
              <Input 
                type="number"
                min="0"
                step="0.01"
                value={newInvoice.amount}
                onChange={(e) => setNewInvoice({...newInvoice, amount: parseFloat(e.target.value) || 0})}
                placeholder="0.00"
              />
            </div>
            <div>
              <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>Type</label>
              <Select value={newInvoice.type} onValueChange={(v) => setNewInvoice({...newInvoice, type: v})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="subscription">Subscription</SelectItem>
                  <SelectItem value="usage">Usage (Pay-As-You-Go)</SelectItem>
                  <SelectItem value="custom">Custom</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>Description</label>
              <Input 
                value={newInvoice.description}
                onChange={(e) => setNewInvoice({...newInvoice, description: e.target.value})}
                placeholder="Invoice description..."
              />
            </div>
            <div>
              <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>Due Date</label>
              <Input 
                type="date"
                value={newInvoice.due_date}
                onChange={(e) => setNewInvoice({...newInvoice, due_date: e.target.value})}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
            <Button onClick={createInvoice} disabled={creating}>
              {creating && <RefreshCw className="w-4 h-4 animate-spin mr-2" />}
              Create Invoice
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Usage Details Dialog */}
      <Dialog open={showUsageDialog} onOpenChange={setShowUsageDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Usage Details - {selectedUsage?.company_name}</DialogTitle>
            <DialogDescription>{selectedUsage?.period}</DialogDescription>
          </DialogHeader>
          {selectedUsage && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-3 gap-4">
                <div className={`p-4 rounded-lg ${isDark ? 'bg-slate-700' : 'bg-gray-100'}`}>
                  <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Total Seats</p>
                  <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{selectedUsage.total_seats?.toLocaleString()}</p>
                </div>
                <div className={`p-4 rounded-lg ${isDark ? 'bg-slate-700' : 'bg-gray-100'}`}>
                  <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Per Seat Rate</p>
                  <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{formatCurrency(selectedUsage.per_seat_rate || 0.50)}</p>
                </div>
                <div className={`p-4 rounded-lg ${isDark ? 'bg-slate-700' : 'bg-gray-100'}`}>
                  <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Total Amount</p>
                  <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{formatCurrency(selectedUsage.total_amount)}</p>
                </div>
              </div>
              
              <div>
                <h4 className={`font-medium mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>Breakdown by Event</h4>
                <div className={`rounded-lg border ${isDark ? 'border-slate-600' : 'border-gray-200'}`}>
                  {selectedUsage.events?.map((event, idx) => (
                    <div key={idx} className={`p-3 flex items-center justify-between ${idx > 0 ? (isDark ? 'border-t border-slate-600' : 'border-t border-gray-200') : ''}`}>
                      <span className={isDark ? 'text-slate-300' : 'text-gray-700'}>{event.name}</span>
                      <span className={isDark ? 'text-white' : 'text-gray-900'}>{event.seats} seats - {formatCurrency(event.amount)}</span>
                    </div>
                  )) || <p className={`p-4 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>No event breakdown available</p>}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUsageDialog(false)}>Close</Button>
            <Button>
              <Send className="w-4 h-4 mr-2" /> Generate & Send Bill
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
